import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Lightbulb,
  Shield,
  GraduationCap,
  Globe,
  Target,
  Rocket,
  Heart,
  CheckCircle,
  ArrowRight,
  Users,
  Zap,
  TrendingUp,
  Brain,
  Sparkles,
  Code2,
  Clock,
  Star,
} from "lucide-react";
import FooterNew from "../components/FooterNew";

const coreValues = [
  {
    icon: Lightbulb,
    title: "Innovation Excellence",
    color: "#7C4DFF",
    desc: "We don't just follow trends — we set them. Our R&D team constantly explores emerging AI paradigms to deliver tomorrow's solutions today.",
    points: [
      "Continuous research and development",
      "Cutting-edge technology adoption",
      "Forward-thinking solutions",
      "Pushing AI boundaries",
    ],
  },
  {
    icon: Heart,
    title: "Client Success",
    color: "#27B8FF",
    desc: "Your growth is our metric. We build long-term partnerships rooted in trust, transparency, and measurable business outcomes.",
    points: [
      "Solution-oriented approach",
      "Measurable business impact",
      "Long-term partnerships",
      "Transparent communication",
    ],
  },
  {
    icon: Shield,
    title: "Ethical AI",
    color: "#A78BFA",
    desc: "We champion responsible AI that is fair, explainable, and privacy-first — because powerful technology demands powerful accountability.",
    points: [
      "Responsible development",
      "Bias mitigation",
      "Data privacy first",
      "Transparent algorithms",
    ],
  },
];

const impactData = [
  { value: "25+", label: "AI Solutions Deployed", icon: Rocket },
  { value: "15+", label: "Industry Verticals", icon: Globe },
  { value: "50+", label: "AI Experts", icon: Users },
  { value: "100%", label: "Client Retention", icon: Star },
  { value: "24/7", label: "Support Uptime", icon: Clock },
  { value: "3M+", label: "Documents Processed", icon: Zap },
];

const timeline = [
  { month: "October 2025", title: "The Inception & Incorporation", desc: "CLIENTCODE AI INNOVATION was officially incorporated with a bold mission — delivering enterprise-grade AI, LLM workflows, and intelligent automation to businesses of all sizes." },
  { month: "December 2025", title: "Core AI Engine & Architecture", desc: "Engineered proprietary RAG pipelines, fine-tuned domain-specific LLMs, and launched our foundational conversational AI and process automation frameworks." },
  { month: "February 2026", title: "Enterprise Solutions & Platforms", desc: "Successfully deployed custom AI platforms, enterprise web portals, and automated data extraction systems for corporate clients." },
  { month: "April 2026", title: "Autonomous Agents & Vision AI", desc: "Expanded core capabilities into autonomous AI agents, computer vision document intelligence, and real-time business analytics dashboards." },
  { month: "June 2026", title: "Creative AI & Media Production", desc: "Launched specialized AI content generation, copy engineering, intelligent video editing, and cinematic video production services." },
  { month: "Present & Beyond", title: "Global Scale & Next-Gen AI", desc: "Powering innovative AI software solutions, fostering industry-ready talent through internships, and pushing the boundaries of generative AI worldwide." },
];

const whyUsCards = [
  {
    icon: Brain,
    title: "AI-Native Team",
    desc: "80% of our engineers hold advanced degrees in AI/ML. We live and breathe artificial intelligence.",
    color: "#7C4DFF",
  },
  {
    icon: Sparkles,
    title: "Generative AI Pioneers",
    desc: "Early adopters and builders of LLM, RAG, and agentic AI systems — not just integrators.",
    color: "#27B8FF",
  },
  {
    icon: Code2,
    title: "End-to-End Delivery",
    desc: "From ideation to production deployment and ongoing optimization — we own the full lifecycle.",
    color: "#A78BFA",
  },
  {
    icon: TrendingUp,
    title: "Measurable ROI",
    desc: "Every solution is benchmarked against business KPIs. We don't ship demos — we ship results.",
    color: "#F472B6",
  },
];

const glassCard = {
  background: "rgba(10, 16, 41, 0.4)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(124, 77, 255, 0.1)",
};

const About = () => {
  return (
    <main className="bg-primary">
      {/* Hero */}
      <section className="relative pt-24 sm:pt-28 pb-8 sm:pb-10 overflow-hidden">
        <div className="absolute inset-0">
          <div
            className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full opacity-[0.12] blur-[100px]"
            style={{ background: "radial-gradient(circle, #7C4DFF, transparent)" }}
          />
          <div
            className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full opacity-[0.08] blur-[100px]"
            style={{ background: "radial-gradient(circle, #27B8FF, transparent)" }}
          />
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-[0.04] blur-[120px]"
            style={{ background: "radial-gradient(circle, #A78BFA, transparent)" }}
          />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-[2.5rem] font-bold text-white mb-5 leading-tight">
              We Build <span className="gradient-text">Intelligent Systems</span> That Transform Businesses
            </h1>
            <p className="text-base sm:text-lg text-slate-400 max-w-3xl mx-auto mb-8 leading-relaxed">
              CLIENTCODE AI INNOVATION is at the forefront of enterprise AI — empowering organizations with Generative AI, Machine Learning, and end-to-end intelligent automation that delivers real, measurable impact.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="neon-btn neon-btn-primary no-underline">
                <Rocket className="w-4 h-4" />
                Start Your AI Journey
              </Link>
              <Link to="/services" className="neon-btn neon-btn-outline no-underline">
                Explore Services
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Story with Timeline */}
      <section className="section-padding relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 sm:mb-10"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Our <span className="gradient-text">Journey</span>
            </h2>
            <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
              From our inception to an industry-leading AI company — here's how our journey unfolds month by month.
            </p>
          </motion.div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px" style={{ background: "linear-gradient(180deg, #7C4DFF, #27B8FF, #A78BFA)" }} />

            <div className="space-y-8 md:space-y-0">
              {timeline.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -40 : 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className={`relative md:flex md:items-center md:mb-12 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
                >
                  {/* Content */}
                  <div className={`md:w-[calc(50%-2rem)] ${i % 2 === 0 ? "md:pr-8" : "md:pl-8"} text-left`}>
                    <div
                      className="rounded-2xl p-6 text-left group hover:-translate-y-1 transition-transform duration-300"
                      style={glassCard}
                    >
                      <span className="text-sm font-bold gradient-text">{item.month || item.year}</span>
                      <h3 className="text-lg font-bold text-white mt-1 mb-2">{item.title}</h3>
                      <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                  {/* Dot */}
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-2 border-accent bg-primary z-10" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl p-8 sm:p-10 relative overflow-hidden"
              style={{ ...glassCard, border: "1px solid rgba(124, 77, 255, 0.15)" }}
            >
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10 blur-3xl" style={{ background: "#7C4DFF" }} />
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-[#7C4DFF]/10 border border-[#7C4DFF]/30 flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-[#7C4DFF]" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
                <p className="text-slate-400 leading-relaxed mb-6">
                  To democratize enterprise-grade AI for businesses of every size — transforming complex challenges into intelligent, scalable, and ethical solutions that drive measurable growth.
                </p>
                <div className="space-y-3">
                  {[
                    "Solve complex business challenges with AI",
                    "Deliver measurable ROI on every engagement",
                    "Make cutting-edge AI accessible and practical",
                    "Build long-term technology partnerships",
                  ].map((text, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-[#7C4DFF] flex-shrink-0" />
                      <span className="text-sm text-slate-300">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl p-8 sm:p-10 relative overflow-hidden"
              style={{ ...glassCard, border: "1px solid rgba(39, 184, 255, 0.15)" }}
            >
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10 blur-3xl" style={{ background: "#27B8FF" }} />
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-[#27B8FF]/10 border border-[#27B8FF]/30 flex items-center justify-center mb-6">
                  <Sparkles className="w-7 h-7 text-[#27B8FF]" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
                <p className="text-slate-400 leading-relaxed mb-6">
                  To be the most trusted AI innovation partner for enterprises worldwide — pioneering responsible, human-centric artificial intelligence that amplifies human potential and reshapes industries.
                </p>
                <div className="space-y-3">
                  {[
                    "Lead the next wave of AI-driven transformation",
                    "Set global standards for ethical AI development",
                    "Empower a million businesses with intelligent automation",
                    "Bridge the gap between AI research and real-world impact",
                  ].map((text, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-[#27B8FF] flex-shrink-0" />
                      <span className="text-sm text-slate-300">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 sm:mb-10"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              What Sets Us <span className="gradient-text">Apart</span>
            </h2>
            <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
              We're not just another IT company that added "AI" to the name. Artificial intelligence is our DNA.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyUsCards.map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                whileHover={{ y: -8 }}
                className="rounded-2xl p-6 text-center group"
                style={glassCard}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform"
                  style={{ background: `${card.color}15`, border: `1px solid ${card.color}30` }}
                >
                  <card.icon className="w-7 h-7" style={{ color: card.color }} />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{card.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 sm:mb-10"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Our Core <span className="gradient-text">Values</span>
            </h2>
            <p className="text-sm sm:text-base text-slate-400 max-w-lg mx-auto leading-relaxed">
              The principles that guide everything we do
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {coreValues.map((val, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                whileHover={{ y: -6 }}
                className="rounded-2xl p-8 group"
                style={glassCard}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"
                  style={{ background: `${val.color}15`, border: `1px solid ${val.color}30` }}
                >
                  <val.icon className="w-7 h-7" style={{ color: val.color }} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{val.title}</h3>
                <p className="text-sm text-slate-400 mb-5 leading-relaxed">{val.desc}</p>
                <div className="w-10 h-0.5 mb-4 rounded-full" style={{ background: val.color }} />
                <ul className="space-y-2 list-none p-0 m-0">
                  {val.points.map((pt, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-slate-400">
                      <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: val.color }} />
                      {pt}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Numbers */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full opacity-[0.06] blur-[100px]" style={{ background: "linear-gradient(90deg, #7C4DFF, #27B8FF)" }} />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 sm:mb-10"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Our Impact in <span className="gradient-text">Numbers</span>
            </h2>
            <p className="text-sm sm:text-base text-slate-400 max-w-xl mx-auto leading-relaxed">
              Real metrics that reflect our commitment to delivering exceptional AI solutions.
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {impactData.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                whileHover={{ scale: 1.05 }}
                className="rounded-2xl p-6 text-center group"
                style={glassCard}
              >
                <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                  <item.icon className="w-5 h-5 text-ai-glow" />
                </div>
                <div className="text-3xl sm:text-4xl font-bold gradient-text mb-2">{item.value}</div>
                <div className="text-sm text-slate-400">{item.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 sm:mb-10"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Our <span className="gradient-text">Expertise</span>
            </h2>
            <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Our team comprises seasoned AI specialists, data scientists, and
              industry experts with diverse backgrounds.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: GraduationCap,
                title: "Technical Excellence",
                color: "#7C4DFF",
                items: [
                  "Advanced Degrees: 80% hold master's or PhDs in AI",
                  "Active contributions to AI research",
                  "Industry certifications across cloud & AI",
                  "Deep knowledge in GenAI, ML, NLP, CV",
                ],
              },
              {
                icon: Globe,
                title: "Industry Experience",
                color: "#27B8FF",
                items: [
                  "Cross-Industry: Finance, Healthcare, Retail & more",
                  "Global project experience",
                  "Understanding of tech & business outcomes",
                  "Led AI digital transformation initiatives",
                ],
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                whileHover={{ y: -4 }}
                className="rounded-2xl p-8"
                style={glassCard}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: `${card.color}15`, border: `1px solid ${card.color}30` }}
                  >
                    <card.icon className="w-6 h-6" style={{ color: card.color }} />
                  </div>
                  <h3 className="text-xl font-bold text-white">{card.title}</h3>
                </div>
                <ul className="space-y-3 list-none p-0 m-0">
                  {card.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-400">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: card.color }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      <FooterNew />
    </main>
  );
};

export default About;
